import { Link } from "@tanstack/react-router";
import CircleBuddy from "./CircleBuddy";

interface NotFoundProps {
  title?: string;
  description?: string;
  /**
   * True when this renders inside the caderno shell (an unknown path under a
   * caderno, which keeps the sidebar). It then sits in the content column rather
   * than owning the screen: no full-height grid and — the reason it is a flag —
   * no `bg-surface`, so the shell's ambient wash shows through instead of a solid
   * block painted over it. The `.lume-page` scope is what flips the semantic
   * tokens under the dark theme, so the text is not dark-on-dark on the shell.
   */
  embedded?: boolean;
}

/**
 * Shown when a URL points at something that does not exist — or at someone
 * else's content. The two cases are deliberately indistinguishable, here and in
 * the API, so ids can't be probed.
 *
 * Same voice as ErrorState: the sad CircleBuddy leads, the message sits below it
 * on the page (no card frame), and the way out is the loudest thing. Where
 * ErrorState offers a retry, this offers the way home — retrying a URL that was
 * never theirs can't change the answer.
 */
export default function NotFound({
  title = "Não encontramos esta página",
  description = "O endereço pode estar errado, ou este conteúdo não é seu.",
  embedded = false,
}: NotFoundProps) {
  const inner = (
    <div className="flex w-full max-w-[380px] animate-rise flex-col items-center text-center">
      <CircleBuddy aria-hidden="true" mood="sad" size={132} />

      <h1 className="mt-4 text-[18px]/tight font-bold text-ink">{title}</h1>
      <p className="mt-2 text-[14px]/relaxed font-medium text-balance text-ink-soft">
        {description}
      </p>

      <Link
        className="mt-6 inline-flex items-center rounded-full bg-main-500 px-6 py-3 text-[14px]/none font-semibold text-white shadow-[0_10px_24px_-6px_rgba(62,69,235,0.5)] transition hover:bg-main-600 hover:shadow-[0_12px_28px_-6px_rgba(62,69,235,0.6)]"
        to="/"
      >
        Voltar ao início
      </Link>
    </div>
  );

  if (embedded) {
    return (
      <div className="lume-page grid min-h-[55vh] place-items-center px-6 py-10 text-ink">
        {inner}
      </div>
    );
  }

  return (
    <div className="grid min-h-svh place-items-center bg-surface px-6 font-sans text-ink antialiased">
      {inner}
    </div>
  );
}
